/*
 * What the portal visualizer draws: a row of frequency bands, each 0..1. On the web they come from the analyser.
 * On Android the sound plays in a native service that Web Audio cannot hear, so `simulateBands` shapes them after
 * the recipe of the sound instead (rain bright and flickering, brown dark and steady, ocean swelling every ~9 s).
 */
import { SleepSound } from './soundEngine';

export const BANDS = 64;

/** Same rate as the ocean's swell in soundEngine.ts and NoiseSynth.java. */
const OCEAN_SWELL_HZ = 0.11;

/** Folds linear FFT bins into log-spaced bands, closer to how pitch is heard. */
export const bandsFromBins = (bins: Uint8Array, out: Float32Array): number => {
    const usable = Math.max(1, Math.floor(bins.length * 0.75));
    let total = 0;
    for (let band = 0; band < out.length; band++) {
        const from = Math.floor(Math.pow(usable, band / out.length));
        const to = Math.max(
            from + 1,
            Math.floor(Math.pow(usable, (band + 1) / out.length))
        );
        let sum = 0;
        for (let bin = from; bin < to; bin++) sum += bins[bin] ?? 0;
        out[band] = sum / (to - from) / 255;
        total += sum;
    }
    return total;
};

const SHAPES: Record<SleepSound, (x: number) => number> = {
    rain: (x) => 0.35 + 0.5 * Math.exp(-Math.pow((x - 0.6) / 0.28, 2)),
    brown: (x) => 0.75 * Math.pow(1 - x, 1.4) + 0.08,
    ocean: (x) => 0.85 * Math.pow(1 - x, 1.8) + 0.05,
};

/** `sound` null is the resting portal: faint and slow. */
export const simulateBands = (
    sound: SleepSound | null,
    seconds: number,
    out: Float32Array
): void => {
    const speed = sound === 'rain' ? 2.6 : sound ? 0.9 : 0.35;
    const swell =
        sound === 'ocean'
            ? 0.6 + 0.4 * Math.sin(2 * Math.PI * OCEAN_SWELL_HZ * seconds)
            : 1;
    for (let band = 0; band < out.length; band++) {
        const x = band / Math.max(1, out.length - 1);
        const shape = sound ? SHAPES[sound](x) : 0.16;
        const motion =
            0.62 +
            0.22 * Math.sin(seconds * speed * 1.3 + band * 0.73) +
            0.16 * Math.sin(seconds * speed * 2.9 - band * 1.91);
        out[band] = Math.min(1, Math.max(0, shape * motion * swell));
    }
};
